import React from "react";
import { formatDateTimeDDMMYYYY, formatDateDDMMYYYY, formatDateTimeDDMMYYYY12Hour } from "../../../helpers/dateUtils";
import { getAmountColorClass } from "./BidTranjections";
import { RO_RED_NUMBERS, RO_BLACK_NUMBERS } from "../../../common/constants";

export const GeneralPnLColumns = (onShowWinners) => [
    {
        name: "Date/Time",
        selector: row => row.createdAt,
        sortable: true,
        cell: row => row.createdAt
    },
    {
        name: "Round Number",
        selector: row => row.roundNumber,
        sortable: true,
        cell: row => (
            <div className="d-flex align-items-center">
                <div style={{ width: '30px' }} className="me-1 d-flex align-items-center justify-content-center">
                    {row.isJokerRound ? (
                        <img
                            src="/joker-face.png"
                            alt="Joker"
                            title={`Joker Applied (${row.jokerMultiplierApplied}x)`}
                            style={{
                                width: '30px',
                                height: '30px',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <div style={{ width: '30px' }} />
                    )}
                </div>
                <span>#{row.roundNumber}</span>
            </div>
        )
    },
    {
        name: "Winning No.",
        selector: row => row.winningNumber,
        sortable: true,
        cell: row => {
            const num = row.winningNumber;
            if (num === undefined || num === null) return '-';
            return <span className="badge bg-secondary">{num}</span>;
        }
    },
    {
        name: "Winner",
        selector: row => {
            // For sorting, return the first winner's email or account number or empty string
            if (row.winners && Array.isArray(row.winners) && row.winners.length > 0) {
                return row.winners[0].email || row.winners[0].acnumber || '';
            }
            return '';
        },
        sortable: false,
        cell: row => {
            // Check for winners array (plural) from API response
            if (row.winners && Array.isArray(row.winners) && row.winners.length > 0) {
                const firstWinner = row.winners[0];
                const email = firstWinner.email || firstWinner.acnumber || firstWinner.userId || 'Unknown';
                const hasMultipleWinners = row.winners.length > 1;

                return (
                    <div className="d-flex flex-column">
                        <span className="fw-bold">{email}</span>
                        {hasMultipleWinners && onShowWinners && (
                            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                (
                                <span
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onShowWinners(row.winners, row.roundNumber);
                                    }}
                                    className="text-primary"
                                    style={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    +{row.winners.length - 1} more
                                </span>
                                )
                            </span>
                        )}
                    </div>
                );
            }
            // Fallback: check for singular winner (for backward compatibility)
            if (row.winner) {
                const name = row.winner.name || row.winner.email || row.winner;
                return (
                    <div>
                        <span className="fw-bold">{name}</span>
                    </div>
                );
            }
            return "-";
        }
    },
    {
        name: "Game Mode",
        selector: row => row.gameMode,
        sortable: true,
        cell: row => {
            const mode = row.gameMode || 'easy';
            let color = 'secondary';
            let label = 'Easy';

            if (mode === 'hard') {
                color = 'danger';
                label = 'Hard';
            } else if (mode === 'more_easy' || mode === 'very_easy' || mode === 'easy_plus') {
                color = 'success';
                label = 'More Easy';
            } else if (mode === 'medium') {
                color = 'warning';
                label = 'Medium';
            } else if (mode === 'easy') {
                color = 'info';
                label = 'Easy';
            }

            return <span className={`badge bg-${color}`}>{label}</span>;
        }
    },
    {
        name: "Total Bets",
        selector: row => row.totalBidsAmount,
        sortable: true,
        cell: row => {
            const amount = row.totalBidsAmount || 0;
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(amount);
        }
    },
    {
        name: "Total Payout",
        selector: row => row.totalWinningsPaid,
        sortable: true,
        cell: row => {
            const amount = row.totalWinningsPaid || 0;
            return (
                <span className={`fw-bold ${getAmountColorClass(amount)}`}>
                    {new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }).format(amount)}
                </span>
            );
        }
    },
    {
        name: "Admin P&L",
        selector: row => row.adminEarnings,
        sortable: true,
        cell: row => {
            const amount = row.adminEarnings || 0;
            return (
                <span className={`fw-bold ${getAmountColorClass(amount)}`}>
                    {new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }).format(amount)}
                </span>
            );
        }
    }
];

export const ROPnLColumns = (onShowWinners) => [
    {
        name: "Date/Time",
        selector: row => row.createdAt,
        sortable: true,
        cell: row => formatDateTimeDDMMYYYY12Hour(row.createdAt)
    },
    {
        name: "Round Number",
        selector: row => row.roundNumber,
        sortable: true,
        cell: row => <span>#{row.roundNumber}</span>
    },
    {
        name: "Winning No.",
        selector: row => row.winningNumber,
        sortable: true,
        cell: row => {
            const num = row.winningNumber;
            if (num === undefined || num === null) return '-';

            const isRed = RO_RED_NUMBERS.includes(Number(num));
            const isBlack = RO_BLACK_NUMBERS.includes(Number(num));
            const isGreen = ["0", "00"].includes(String(num));

            return (
                <span
                    className="badge rounded-circle d-inline-flex align-items-center justify-content-center text-white fw-bold"
                    style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: isRed ? '#ef4444' : isBlack ? '#111827' : isGreen ? '#10b981' : '#6b7280'
                    }}
                >
                    {num}
                </span>
            );
        }
    },
    {
        name: "Winners",
        selector: row => {
            if (row.winners && Array.isArray(row.winners) && row.winners.length > 0) {
                return row.winners[0].email || row.winners[0].acnumber || '';
            }
            return '';
        },
        sortable: false,
        cell: row => {
            if (row.winners && Array.isArray(row.winners) && row.winners.length > 0) {
                const firstWinner = row.winners[0];
                const email = firstWinner.email || firstWinner.acnumber || firstWinner.userId || 'Unknown';
                const hasMultipleWinners = row.winners.length > 1;

                return (
                    <div className="d-flex flex-column">
                        <span className="fw-bold">{email}</span>
                        {hasMultipleWinners && onShowWinners && (
                            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                (
                                <span
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onShowWinners(row.winners, row.roundNumber);
                                    }}
                                    className="text-primary"
                                    style={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    +{row.winners.length - 1} more
                                </span>
                                )
                            </span>
                        )}
                    </div>
                );
            }
            return "-";
        }
    },
    {
        name: "Game Mode",
        selector: row => row.gameMode,
        sortable: true,
        cell: row => {
            const mode = row.gameMode || 'easy';
            let color = 'secondary';
            let label = 'Easy';

            if (mode === 'hard') {
                color = 'danger';
                label = 'Hard';
            } else if (mode === 'more_easy' || mode === 'very_easy' || mode === 'easy_plus') {
                color = 'success';
                label = 'More Easy';
            } else if (mode === 'medium') {
                color = 'warning';
                label = 'Medium';
            } else if (mode === 'easy') {
                color = 'info';
                label = 'Easy';
            }

            return <span className={`badge bg-${color}`}>{label}</span>;
        }
    },
    {
        name: "Total Bets",
        selector: row => row.totalBidsAmount,
        sortable: true,
        cell: row => {
            const amount = row.totalBidsAmount || 0;
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(amount);
        }
    },
    {
        name: "Total Payout",
        selector: row => row.totalWinningsPaid,
        sortable: true,
        cell: row => {
            const amount = row.totalWinningsPaid || 0;
            return (
                <span className={`fw-bold ${getAmountColorClass(amount)}`}>
                    {new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }).format(amount)}
                </span>
            );
        }
    },
    {
        name: "Admin P&L",
        selector: row => row.adminEarnings,
        sortable: true,
        cell: row => {
            const amount = row.adminEarnings || 0;
            return (
                <span className={`fw-bold ${getAmountColorClass(amount)}`}>
                    {new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }).format(amount)}
                </span>
            );
        }
    }

];


export const JokerPnLColumns = (onTransactionClick) => [
    {
        name: "Date/Time",
        selector: row => row.startTime,
        sortable: true,
        cell: row => {
            return formatDateDDMMYYYY(row.startTime);
        }
    },
    {
        name: "Rounds (Triggered / Configured)",
        selector: row => `${row.roundsTriggered || 0} / ${row.roundsConfigured || 0}`,
        sortable: false,
        cell: row => {
            const triggered = row.roundsTriggered || 0;
            const configured = row.roundsConfigured || 0;
            const percentage = configured > 0 ? Math.round((triggered / configured) * 100) : 0;

            return (
                <div className="d-flex flex-column">
                    <span className="fw-semibold">
                        <span className="text-success">{triggered}</span>
                        <span className="text-muted mx-1">/</span>
                        <span className="text-muted">{configured}</span>
                    </span>
                    {configured > 0 && (
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {percentage}% hit rate
                        </small>
                    )}
                </div>
            );
        }
    },
    {
        name: "Multiplier",
        selector: row => row.multiplier,
        sortable: true,
        cell: row => <span className="badge bg-secondary">{row.multiplier}x</span>
    },
    {
        name: "Status",
        selector: row => row.status,
        sortable: true,
        cell: row => {
            let color = "secondary";
            if (row.status === "running") color = "warning";
            if (row.status === "completed") color = "success";
            if (row.status === "stop") color = "info";
            if (row.status === "expire") color = "danger";
            return <span className={`badge bg-${color}`}>{row.status?.toUpperCase()}</span>
        }
    },
    {
        name: "Total Bets",
        selector: row => row.totalBets,
        sortable: true
    },
    {
        name: "Total Payout",
        selector: row => row.totalPayout,
        sortable: true
    },
    {
        name: "Action",
        selector: row => row._id || row.id,
        sortable: false,
        cell: row => (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    if (onTransactionClick) onTransactionClick(row._id || row.id);
                }}
                className="text-decoration-none"
                title="View Transactions"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#3577f1',
                    borderRadius: '4px',
                    color: '#ffffff'
                }}
            >
                <i className="ri-history-line" style={{ fontSize: '16px' }}></i>
            </a>
        )
    }
];

export const DayReportColumns = (onShowWinners) => [
    {
        name: "Game",
        selector: row => row.gameDisplayName,
        sortable: true,
        cell: row => <span className="fw-medium">{row.gameDisplayName}</span>
    },
    {
        name: "Round ID",
        selector: row => row.roundNumber,
        sortable: true,
        cell: row => (
            <div className="d-flex align-items-center">
                <div style={{ width: '30px' }} className="me-1 d-flex align-items-center justify-content-center">
                    {row.isJokerRound ? (
                        <img
                            src="/joker-face.png"
                            alt="Joker"
                            title={`Joker Applied (${row.jokerMultiplierApplied}x)`}
                            style={{
                                width: '30px',
                                height: '30px',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <div style={{ width: '30px' }} />
                    )}
                </div>
                <span className="fw-medium">#{row.roundNumber}</span>
            </div>
        )
    },
    {
        name: "Win No.",
        selector: row => row.winningNumber,
        sortable: true,
        cell: row => <span className="badge bg-secondary">{row.winningNumber !== undefined ? row.winningNumber : '-'}</span>
    },
    {
        name: "Winner",
        selector: row => row.winners?.length || 0,
        sortable: false,
        cell: row => {
            if (row.winners && row.winners.length > 0) {
                const firstWinner = row.winners[0];
                const displayId = firstWinner.accountId && firstWinner.accountId !== "N/A"
                    ? firstWinner.accountId
                    : (firstWinner.acnumber || firstWinner.userId);

                const hasMore = row.winners.length > 1;

                return (
                    <div className="d-flex flex-column align-items-start">
                        <span
                            className="fw-bold text-dark"
                            style={{ fontSize: '13px', cursor: 'pointer' }}
                            onClick={() => onShowWinners(row.winners, row.roundNumber)}
                        >
                            {displayId}
                        </span>
                        {hasMore && (
                            <span
                                className="text-primary"
                                style={{ fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => onShowWinners(row.winners, row.roundNumber)}
                            >
                                (+{row.winners.length - 1} more)
                            </span>
                        )}
                    </div>
                );
            }
            return "-";
        }
    },
    {
        name: "Total bets",
        selector: row => row.totalBidsAmount,
        sortable: true,
        cell: row => {
            const amount = row.totalBidsAmount || 0;
            return new Intl.NumberFormat('en-US').format(amount);
        }
    },
    {
        name: "Total Payout",
        selector: row => row.totalWinningsPaid,
        sortable: true,
        cell: row => {
            const amount = row.totalWinningsPaid || 0;
            return (
                <span className={`fw-bold ${getAmountColorClass(amount)}`}>
                    {new Intl.NumberFormat('en-US').format(amount)}
                </span>
            );
        }
    },
    {
        name: "Time",
        selector: row => row.createdAt,
        sortable: true,
        cell: row => <span style={{ fontSize: '12px' }}>{row.createdAt}</span>
    }
];